import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixAnalyticsSemanticModelV3SemanticModelsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    '*.dev.wix-code.com': [
      {
        srcPath: '/analytics/semantic-model/v3/semantic-models',
        destPath: '/v3/semantic-models',
      },
    ],
    _: [
      {
        srcPath: '/analytics/semantic-model/v3/semantic-models',
        destPath: '/v3/semantic-models',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/analytics/semantic-model/v3/semantic-models',
        destPath: '/v3/semantic-models',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME =
  '@wix/auto_sdk_analytics-semantic-model_analytics-semantic-model';

/**
 * Retrieves data from a semantic model for the specified fields, filters, sorting, and paging.
 *
 *
 * Query Semantic Model Data runs with these defaults, which you can override:
 *
 * - `paging.limit` is `50`
 * - `paging.offset` is `0`
 *
 * To learn about working with Query methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language), [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging), and [Field Projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection).
 */
export function querySemanticModelData(
  payload: object
): RequestOptionsFactory<any> {
  function __querySemanticModelData({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'interval.start' }, { path: 'interval.end' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.analytics.semantic_model.v3.semantic_model',
      method: 'POST' as any,
      methodFqn:
        'wix.analytics.semantic_model.v3.SemanticModelsService.QuerySemanticModelData',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAnalyticsSemanticModelV3SemanticModelsServiceUrl({
        protoPath: '/v3/semantic-models/query-data',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'results.fields.*.numericValue' },
              { path: 'totals.fields.*.numericValue' },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.fields.*.timestampValue' },
              { path: 'totals.fields.*.timestampValue' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __querySemanticModelData;
}

/** Retrieves a semantic model by ID. */
export function getSemanticModel(payload: object): RequestOptionsFactory<any> {
  function __getSemanticModel({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.analytics.semantic_model.v3.semantic_model',
      method: 'GET' as any,
      methodFqn:
        'wix.analytics.semantic_model.v3.SemanticModelsService.GetSemanticModel',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAnalyticsSemanticModelV3SemanticModelsServiceUrl({
        protoPath: '/v3/semantic-models/{semanticModelId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getSemanticModel;
}

/** Retrieves a list of all available semantic models. */
export function listSemanticModels(
  payload: object
): RequestOptionsFactory<any> {
  function __listSemanticModels({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.analytics.semantic_model.v3.semantic_model',
      method: 'GET' as any,
      methodFqn:
        'wix.analytics.semantic_model.v3.SemanticModelsService.ListSemanticModels',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAnalyticsSemanticModelV3SemanticModelsServiceUrl({
        protoPath: '/v3/semantic-models',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __listSemanticModels;
}
